import { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import { Flex } from '@chakra-ui/react';
import {
  FormBuilder,
  FieldRegistryImpl,
  type FormBuilderHandle,
  type FormSchema,
} from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import type { InterviewActivityStepProps } from '../types';
import { useInterviewStageManager } from '@/pages/ats-job-creation/tabs/interview-plan/stage/stage-manager';
import { useInterviewActivityDetailsSchema } from './useInterviewActivityDetailsSchema';
import { VariableInputAdapter } from './VariableInputAdapter';

const detailsFieldRegistry = new FieldRegistryImpl().register(
  'variableInput',
  VariableInputAdapter
);

export const InterviewActivityDetails = memo(
  forwardRef<StepFormApi, InterviewActivityStepProps>(
    (
      { payloadRef, prefillVersion = 0, selectedActivity, noteTakerMandatory },
      ref
    ) => {
      const { activityAction } = useInterviewStageManager();
      const { stage = {} } = activityAction as { stage: { name?: string } };

      const participantDefaultOptions = useMemo(() => {
        const raw = (
          payloadRef.current.details as Record<string, unknown> | undefined
        )?.participant_default_options;
        return Array.isArray(raw)
          ? (raw as Array<{
              value: string;
              label: string;
              description?: string;
              image?: string;
            }>)
          : [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [prefillVersion]);

      const {
        schema,
        defaultValues: schemaDefaults,
        defaultLocation,
        defaultCalendar,
      } = useInterviewActivityDetailsSchema({
        stageName: stage?.name,
        selectedActivity,
        participantDefaultOptions,
        noteTakerMandatory,
      });

      const defaultValues = useMemo(() => {
        const rawDetails = (payloadRef.current.details ?? {}) as Record<
          string,
          unknown
        >;
        const { participant_default_options: _omit, ...detailsForForm } =
          rawDetails;
        return {
          ...schemaDefaults,
          ...detailsForForm,
          // Mandatory force-wins. Mirrors legacy
          // `pages/ats-job-creation/.../schedule-interview/Details.tsx:113`:
          //   enable_note_taker: note_taker_mandatory || note_taker_enabled
          // Without this, an existing row saved with
          // `note_taker_enabled: false` (before the job was flipped to
          // mandatory) clobbers `schemaDefaults.enable_note_taker = true`,
          // leaving the disabled toggle stuck OFF while the helper text
          // says "AI notetaker is mandatory for this job."
          enable_note_taker:
            schemaDefaults.enable_note_taker === true
              ? true
              : Boolean(detailsForForm.enable_note_taker),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [schemaDefaults, prefillVersion]);

      const formRef = useRef<FormBuilderHandle>(null);

      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            if (!formRef.current) return true;
            return formRef.current.trigger();
          },
          syncToPayload: () => {
            if (!formRef.current) return;
            const values = formRef.current.getValues();
            const keep = (
              payloadRef.current.details as Record<string, unknown> | undefined
            )?.participant_default_options;
            payloadRef.current.details = {
              ...values,
              ...(Array.isArray(keep) && keep.length > 0
                ? { participant_default_options: keep }
                : {}),
            };
          },
        }),
        [payloadRef]
      );

      // Remount form when defaults become ready (location API + shared
      // calendars) or edit data loads. The shared-calendar query lands
      // AFTER first paint, so without keying on `defaultCalendar` the
      // form would mount with `calendar_info_picker: ''` and never
      // back-fill. `'loading'` placeholder keeps a stable key while
      // either query is in flight; once both resolve the key flips and
      // the form remounts with the seeded calendar.
      const formKey = `${prefillVersion}-${defaultLocation ?? 'loading'}-${
        defaultCalendar?.provider_calendar_id ?? 'no-cal'
      }`;

      return (
        <Flex
          data-interview-details-step
          justifyContent="center"
          w="100%"
          minH={0}
          flex="1"
          overflowY="auto"
          key={formKey}
          mb="12px"
        >
          <Flex w="560px" maxW="100%" flexDir="column">
            <FormBuilder
              ref={formRef}
              schema={schema as FormSchema}
              defaultValues={defaultValues}
              columns={1}
              mode="onBlur"
              fieldRegistry={detailsFieldRegistry}
              // Re-validate on change so stale "required" errors clear
              // (RHF's onBlur+reValidateMode misses select adapters).
              onFieldChange={(name) => {
                void formRef.current?.trigger(name);
              }}
            />
          </Flex>
        </Flex>
      );
    }
  )
);

InterviewActivityDetails.displayName = 'InterviewActivityDetails';
